import { useCallback, useEffect, useRef } from "react"
import debounce from 'lodash/debounce';

// Listens for scroll and resize events and loads more if we need to.
// Returns a function to trigger a loadMore check immediately.
export function useLoadMore(canLoadMore: boolean, loadMore: () => void): () => void {
    const canLoadMoreRef = useRef(canLoadMore);
    const loadMoreRef = useRef(loadMore);

    // We're using refs here so that we don't have to rebind events every render.
    // When "checkLoadMore" runs, it can access the refs to get the current state.
    canLoadMoreRef.current = canLoadMore;
    loadMoreRef.current = loadMore;

    // Checks scroll positioning to see if we need to load more.
    const checkLoadMore = useCallback(() => {
        if (canLoadMoreRef.current) {
            // See if we're near the bottom of the page.
            const doc = document.documentElement;
            const distFromBottom = doc.offsetHeight - (doc.scrollTop + window.innerHeight);

            if (distFromBottom < 200) {
                loadMoreRef.current();
            }
        }
    }, []);

    const debouncedCheck = useCallback(
        debounce(checkLoadMore, 250, {maxWait: 250}),
        []
    );

    useEffect(() => {
        window.addEventListener('scroll', debouncedCheck, { passive: true });
        return () => window.removeEventListener('scroll', debouncedCheck);
    }, []);

    useEffect(() => {
        window.addEventListener('resize', debouncedCheck);
        return () => window.removeEventListener('resize', debouncedCheck);
    }, []);

    return checkLoadMore;
}